import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import {
  Button,
  Form,
  FormLayout,
  Modal,
  ModalBody,
  ModalFooter,
  Text,
  TextArea,
} from '@customerio/pluma-components/react';
import { authStore } from '@/services/auth/store';
import { useStore } from '@nanostores/react';
import { z } from '@customerio/hydra-validation';

import { useZodForm } from '@/components/form';

import { useSubmitFeedback } from '../hooks/use-submit-feedback';

const feedbackSchema = z.object({
  feedback: z.string().trim().min(1, 'Feedback is required'),
});

interface FeatureFeedbackModalProps {
  feature: string;
  initialFeedback?: string;
  message?: string;
}

export function FeatureFeedbackModal({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<FeatureFeedbackModalProps>) {
  const auth = useStore(authStore);
  const submitFeedback = useSubmitFeedback();

  const form = useZodForm({
    schema: feedbackSchema,
    defaultValues: { feedback: data.initialFeedback ?? '' },
    mode: 'onChange',
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await submitFeedback({
        ...values,
        source: `${data.feature} Feedback`,
        praise: values.feedback,
        criticism: '',
        replyTo: auth.user?.email ?? '',
      });

      onConfirm(values);
    } catch {
      // noop
    }
  });

  const { isValid, isSubmitting } = form.formState;

  if (auth.account == null || auth.user == null) {
    return null;
  }

  return (
    <Modal
      state={modalState}
      onClose={onClose}
      title={`${data.feature} Feedback`}
      size="md"
      shouldCloseOnOverlayClick={false}
    >
      <ModalBody>
        <Form id="feature-feedback-form" onSubmit={onSubmit}>
          <Text mb="200" color="subtle">
            {data.message ??
              `Let us know how we can improve ${data.feature.toLowerCase()}. Your feedback will go directly to the team working on this feature.`}
          </Text>

          <FormLayout mt="200">
            <TextArea
              {...form.registerPluma('feedback')}
              label="Share your feedback"
              placeholder="Share your feedback..."
              minRows={3}
              maxRows={8}
              id="feature-feedback-textarea"
            />
          </FormLayout>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="subtle" onClick={onClose} isDisabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          form="feature-feedback-form"
          type="submit"
          isDisabled={!isValid}
          isLoading={isSubmitting}
        >
          Submit feedback
        </Button>
      </ModalFooter>
    </Modal>
  );
}
